import React, { useState, useEffect } from "react";
import { Button, Modal, Form } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { far } from "@fortawesome/pro-regular-svg-icons";
import { defineMessages } from "react-intl";
import useFormatter from "../hooks/useFormatter";
import { JOB_ENERGYTYPE } from '../constants';
import PropTypes from 'prop-types';
import AlertDialog from "./AlertDialog";
import useServing from "../hooks/useServing";

// This view's strings for translation
const messages = defineMessages({
    actionSave: {
        id: "Tallenna",
        defaultMessage: "Tallenna"
    },
    failedToLoadUsagePlaces: {
        id: "Jotain meni vikaan, ole hyvä ja yritä uudelleen.",
        defaultMessage: "Jotain meni vikaan, ole hyvä ja yritä uudelleen."
    },
    signServing: {
        id: "Allekirjoita tiedoksianto",
        defaultMessage: "Allekirjoita tiedoksianto",
    },
    servingHeader: {
        id: "Ilmoitus kuluttajalle sähkö- tai verkkomaksun maksamatta jättämisestä",
        defaultMessage: "Ilmoitus kuluttajalle sähkö- tai verkkomaksun maksamatta jättämisestä"
    },
    servingParagraph1: {
        id: "Koska et ole maksanut toimitettua sähköä ajoissa, sähkön siirto voi keskeytyä. Ruotsin sähkölain säännösten mukaan voit estää keskeytyksen maksamalla velan kolmen viikon kuluessa tämän ilmoituksen vastaanottamisesta. Kuntasi sosiaalilautakunta saa ilmoituksen maksun myöhästymisestä.",
        defaultMessage: "Koska et ole maksanut toimitettua sähköä ajoissa, sähkön siirto voi keskeytyä. Ruotsin sähkölain säännösten mukaan voit estää keskeytyksen maksamalla velan kolmen viikon kuluessa tämän ilmoituksen vastaanottamisesta. Kuntasi sosiaalilautakunta saa ilmoituksen maksun myöhästymisestä."
    },
    servingHeaderHeating: {
        id: "Ilmoitus kuluttajalle kaukolämmön maksamatta jättämisestä",
        defaultMessage: "Ilmoitus kuluttajalle kaukolämmön maksamatta jättämisestä"
    },
    servingParagraphHeating1: {
        id: "Koska et ole maksanut kaukolämmön maksua ajallaan, kaukolämmön jakelu voidaan keskeyttää. Ruotsin kaukolämpölain säännösten mukaan voit estää keskeyttämisen maksamalla velan kolmen viikon kuluessa tämän ilmoituksen vastaanottamisesta. Sen kunnan sosiaalihuolto, jossa kaukolämpö jaetaan, on saanut ilmoituksen maksamatta jääneestä maksusta.",
        defaultMessage: "Koska et ole maksanut kaukolämmön maksua ajallaan, kaukolämmön jakelu voidaan keskeyttää. Ruotsin kaukolämpölain säännösten mukaan voit estää keskeyttämisen maksamalla velan kolmen viikon kuluessa tämän ilmoituksen vastaanottamisesta. Sen kunnan sosiaalihuolto, jossa kaukolämpö jaetaan, on saanut ilmoituksen maksamatta jääneestä maksusta."
    },
    payee: {
        id: "Velkoja",
        defaultMessage: "Velkoja"
    },
    payer: {
        id: "Velallinen",
        defaultMessage: "Velallinen"
    },
    usageplaceID: {
        id: "Käyttöpaikan tunnus",
        defaultMessage: "Käyttöpaikan tunnus"
    },
    usageplaceAddress: {
        id: "Käyttöpaikan osoite",
        defaultMessage: "Käyttöpaikan osoite"
    },
    debtSpecification: {
        id: "Velan erittely",
        defaultMessage: "Velan erittely"
    },
    jobId: {
        id: "Laskun numero",
        defaultMessage: "Laskun numero"
    },
    payDate: {
        id: "Eräpäivä",
        defaultMessage: "Eräpäivä"
    },
    openCapital: {
        id: "Pääoma",
        defaultMessage: "Pääoma"
    },
    openInterest: {
        id: "Viivästyskorko",
        defaultMessage: "Viivästyskorko"
    },
    openExpenses: {
        id: "Kustannukset",
        defaultMessage: "Kustannukset"
    },
    openAmount: {
        id: "Kaikki yhteensä",
        defaultMessage: "Kaikki yhteensä"
    },
    confirmSignature: {
        id: "Tällä vahvistuksella ja allekirjoituksella vakuutan, että olen lukenut Ropo Sweden AB:n minulle antamat tiedot.",
        defaultMessage: "Tällä vahvistuksella ja allekirjoituksella vakuutan, että olen lukenut Ropo Sweden AB:n minulle antamat tiedot."
    },
    backToInvoice: {
        id: "Palaa laskun tietoihin",
        defaultMessage: "Palaa laskun tietoihin"
    },
    unknown: {
        id: "Ei tiedossa",
        defaultMessage: "Ei tiedossa"
    },
    authError: {
        id: "Tunnistautuminen epäonnistui. Ole hyvä ja yritä uudelleen.",
        defaultMessage: "Tunnistautuminen epäonnistui. Ole hyvä ja yritä uudelleen."
    }
});

export { messages };


const ServingModal = (props) => {
    let lng = props.intl.formatMessage;
    const jobPayeeName = props.job.payee.name;
    const jobPayerSsn = props.job.payer.ssn;
    const jobPayerName = props.job.payer.name;
    const jobPayerAddress = props.job.payer.address[0];
    const energytypeHeating = (props.job.energytype === JOB_ENERGYTYPE.HEATING);
    const modalHeader = energytypeHeating ? messages.servingHeaderHeating : messages.servingHeader;
    const modalParagraph = energytypeHeating ? messages.servingParagraphHeating1 : messages.servingParagraph1;
    const [payeeAddressInformation, setPayeeAddressInformation] = useState(lng(messages.unknown));
    const [usageplaceId, setUsageplaceId] = useState('');
    const [usageplaceAddressInformation, setUsageplaceAddressInformation] = useState(lng(messages.unknown));
    const [showAlertDialog, setShowAlertDialog] = useState(false);
    const [alertDialogMessage, setAlertDialogMessage] = useState('');
    const [alertDialogType, setAlertDialogType] = useState('');
    const [isChecked, setIsChecked] = useState(false);
    const { formatDate, formatCurrency } = useFormatter();
    const { fetchUsagePlaceInfo, formatAddresses } = useServing(props.job);


    /**
     * Try to get usage place information for the serving.
     */
    useEffect(() => {
        const getUsagePlace = async () => {
            /**
             * Fetch usage place info and set payee address information.
             * In some cases the usage place info is not found, errror is handled silently,
             * but show general error in dialog, when request fails completely
             */
            try {
                const response = await fetchUsagePlaceInfo(props.job.dpid);
                // set usage place information
                setUsageplaceId(response.id ?? lng(messages.unknown));
                setUsageplaceAddressInformation(response.address ?? lng(messages.unknown));

                // format payee (creditor) address
                setPayeeAddressInformation(formatAddresses(props.job.payee.address[0]));
            } catch (error) {
                // request fails completely, show message in alertdialog
                handleAlertDialog(lng(messages.failedToLoadUsagePlaces), 'warning');
            }
        }

        void getUsagePlace();
    }, [props.job]);


    /**
     * Handle error messages to show in the alertDialog.
     * @param message
     * @param type
     */
    const handleAlertDialog = (message, type) => {
        setAlertDialogMessage(message);
        setAlertDialogType(type);
        setShowAlertDialog(true);
    }

    /**
     * Check for checkbox changes and toggle style classes accordingly
     * @param e
     */
    const handleCheckbox = (e) => {
        if (e.target.form.checkValidity()) {
            e.target.form.classList.remove("error");
        } else {
            e.target.form.classList.add("error");
        }

        setIsChecked(!isChecked);
    }

    /**
     * Handle saving of the signature for the serving.
     * Do not allow saving if checkbox is not checked.
     *
     * @param e
     */
    const signServing = (e) => {
        if (!isChecked) {
            // add error class to inform user
            e.target.form.classList.add('error');
        } else {
            // checkbox is checked, remove error class
            e.target.form.classList.remove('error');
            props.signServing(e);
        }
    }

    /**
     * Close the modal, clear alerts and set the checkbox unchecked.
     */
    const handleHide = () => {
        setIsChecked(false);
        setShowAlertDialog(false);

        if (props.show) {
            props.hide();
        }
    }

    /**
     * Render payee and payer information.
     * @returns {JSX.Element}
     */
    const renderPayeeAndPayer = () => (
        <>
            <ul id='payee-info-list'>
                <li key='payee-info' id='payee-info'>{lng(messages.payee)}: <br />  {jobPayeeName}, {payeeAddressInformation}</li>
            </ul>
            <ul id='payer-info-list'>
                <li id='payer-ssn'>{lng(messages.payer)}: {jobPayerSsn}</li>
                <li id='payer-name'>{jobPayerName}</li>
                <li id='payer-address'>
                    {[
                        jobPayerAddress.address,
                        jobPayerAddress.addressaddline1,
                        jobPayerAddress.addressaddline2,
                        jobPayerAddress.addressaddline3
                    ].filter(Boolean).join(' ')}
                </li>
                <li id='payer-postcode-city'>
                    {jobPayerAddress.postcode} {jobPayerAddress.city}
                </li>
            </ul>
        </>
    );

    /**
     * Render usage place information.
     * @returns {JSX.Element}
     */
    const renderUsagePlace = () => (
        <ul id='usageplace-info-list'>
            <li key='usageplace-id' id='usageplace-id'>{lng(messages.usageplaceID)}: {usageplaceId} </li>
            <li key='usageplace-address' id='usageplace-address'> {lng(messages.usageplaceAddress)}: {usageplaceAddressInformation}
            </li>
        </ul>
    );

    /**
     * Render debt details information.
     * @returns {JSX.Element}
     */
    const renderDebtDetails = () => (
        <>
            <h6 className='mt-4 mb-1'>{lng(messages.debtSpecification)} </h6>
            <ul id='debt-details-list'>
                <li id='debt-details-jobid'>
                    {lng(messages.jobId)}: {props.job.jobid}
                </li>
                <li id='debt-details-paydate'>
                    {lng(messages.payDate)}: {formatDate(props.job.paydate)}
                </li>
                {['openCapital', 'openInterest', 'openExpenses', 'openAmount'].map(field => (
                    <li key={field} id={`debt-details-${field}`}>
                        {lng(messages[field])}:
                        {formatCurrency(props.job[field.toLowerCase()], props.job.currency)}
                    </li>
                ))}
            </ul>
        </>

    );

    /**
     * Check if the job has subjobs and list the debt details for them, too.
     * @returns {JSX.Element}
     */
    const renderSubJobDebtDetails = () => (
        <>
            {props.job.members !== null && (<>
                {props.job.members.map((subjob, i) => (
                    <ul id={`subjob-debt-details-list-${i}`} key={subjob.jobid}>
                        <li key={`subjob-${subjob.jobid}-jobid`} id={`subjob-debt-details-jobid-${i}`}>
                            {lng(messages.jobId)}: {subjob.jobid}
                        </li>
                        <li key={`subjob-${subjob.jobid}-paydate`} id={`subjob-debt-details-paydate-${i}`}>
                            {lng(messages.payDate)}: {formatDate(subjob.paydate)}
                        </li>
                        {['openCapital', 'openInterest', 'openExpenses', 'openAmount'].map(field => (
                            <li key={`subjob-${i}-${field}`} id={`subjob-debt-details-${field}-${i}`}>
                                {lng(messages[field])}:
                                {formatCurrency(subjob[field.toLowerCase()], subjob.currency)}
                            </li>
                        ))}
                    </ul>
                ))}
            </>)
            }
        </>
    );

    return (
        <Modal show={props.show} onHide={handleHide}>
            <Modal.Header closeButton>
                <Button className="modal-back" onClick={handleHide}>
                    <FontAwesomeIcon icon={far.faArrowLeft} /> {lng(messages.backToInvoice)}
                </Button>
                <Modal.Title>{lng(messages.signServing)}</Modal.Title>
            </Modal.Header>
            <Modal.Body className='serving-modal-list-unstyled'>
                <p className="text-primary"> <FontAwesomeIcon icon={far.faInfoCircle} /> &nbsp;
                    {lng(modalHeader).toUpperCase()}
                </p>
                <p>
                    {lng(modalParagraph)}
                </p>
                {showAlertDialog && (
                    <AlertDialog
                        message={alertDialogMessage}
                        type={alertDialogType}
                    />
                )}
                {renderPayeeAndPayer()}
                {renderUsagePlace()}
                {renderDebtDetails()}
                {renderSubJobDebtDetails()}
                <Form id='serving-form' className='pt-4 pt-sm-2' noValidate>
                    <Form.Group controlId='sign-serving'>
                        <Form.Check
                            id="check-serving"
                            type="checkbox"
                            label={lng(messages.confirmSignature)}
                            onChange={(e) => handleCheckbox(e)}
                            value={isChecked}
                            required
                        />
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button form='serving-form' variant="primary" onClick={(e) => signServing(e)}>
                    {lng(messages.actionSave)}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

ServingModal.propTypes = {
    'intl': PropTypes.object.isRequired,
    'show': PropTypes.bool,
    'job': PropTypes.object,
    'signServing': PropTypes.func,
    'hide': PropTypes.func,
};

export default ServingModal;